import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  TrendingUp, TrendingDown, AlertOctagon, Package, TriangleAlert, Users,
  Sparkles, ArrowUpRight, LineChart as LineChartIcon, Boxes, Truck, BarChart3,
  ChevronRight,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { api } from "../lib/api";
import { useSession } from "../context/SessionContext";
import SizedChart from "../components/SizedChart";
import HealthPill from "../components/HealthPill";
import { fmtInr, fmtPct } from "../lib/format";

const BAND_COLOR = { green: "#2F8B5E", amber: "#B8842A", red: "#B7402A" };

const PRIORITY_META = {
  high: { label: "Now",     color: "#B7402A", bg: "rgba(183,64,42,0.10)", border: "rgba(183,64,42,0.35)" },
  med:  { label: "Today",   color: "#B8842A", bg: "rgba(184,132,42,0.10)", border: "rgba(184,132,42,0.35)" },
  low:  { label: "This wk", color: "#2F6E4A", bg: "rgba(47,110,74,0.10)",  border: "rgba(47,110,74,0.30)" },
};

const ACTION_ICON = {
  expiring:  TriangleAlert,
  stockout:  Package,
  staffing:  Users,
  planogram: Sparkles,
};

const MODULE_META = {
  sales:       { label: "Sales",       icon: LineChartIcon, hint: "Trend · categories · footfall" },
  inventory:   { label: "Inventory",   icon: Boxes,         hint: "Stock · expiry · aging" },
  procurement: { label: "Procurement", icon: Truck,         hint: "Reorders · vendors · costs" },
  hr:          { label: "HR & Roster", icon: Users,         hint: "Staff coverage today" },
  analytics:   { label: "Analytics",   icon: BarChart3,     hint: "Chain intelligence" },
};

const StoreManagerOverview = () => {
  const { session, allowedModules } = useSession();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api
      .get("/overview/store", { params: { role: session.role, branch_id: session.branch_id } })
      .then((r) => mounted && setData(r.data))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [session]);

  if (loading || !data) {
    return (
      <div className="ang-panel p-10 text-center text-sm text-ang-muted" data-testid="store-overview-loading">
        Loading your store…
      </div>
    );
  }

  const bandColor = BAND_COLOR[data.health.band] || "#6B6B7B";
  const rankPercentile = Math.round((1 - data.rank / data.total_branches) * 100);
  const jumpModules = ["sales", "inventory", "procurement", "hr"].filter((m) => allowedModules.includes(m));

  return (
    <div className="space-y-6" data-testid="store-manager-overview">
      {/* HERO — branch name, rank, band */}
      <section className="ang-panel overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-[1.6fr_1fr] divide-y md:divide-y-0 md:divide-x divide-ang">
          <div className="p-5 sm:p-7">
            <div className="text-[10px] uppercase tracking-widest text-ang-muted">Your store · today</div>
            <h1 className="font-serif text-3xl sm:text-4xl mt-1 leading-tight" data-testid="sm-branch-name">
              {data.branch.name}
            </h1>
            <div className="text-sm text-ang-muted mt-1">
              {data.branch.region} Chennai
              {data.branch.size_sqft ? ` · ${data.branch.size_sqft.toLocaleString("en-IN")} sqft` : ""}
            </div>
            <div className="flex items-center gap-6 mt-6 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-ang-muted">Health</div>
                <div className="mt-1"><HealthPill score={data.health.score} band={data.health.band} testId="sm-health-pill" /></div>
              </div>
              <div className="border-l border-ang pl-6">
                <div className="text-[10px] uppercase tracking-widest text-ang-muted">Rank in chain</div>
                <div className="font-serif text-3xl mt-0.5 tabular-nums" data-testid="sm-rank">
                  <span style={{ color: bandColor }}>{data.rank}</span>
                  <span className="text-ang-muted text-xl"> / {data.total_branches}</span>
                </div>
                <div className="text-[11px] text-ang-muted mt-0.5">
                  Top {Math.max(1, 100 - rankPercentile)}% · chain avg {data.chain_avg_health}
                </div>
              </div>
            </div>
          </div>

          {/* Right: revenue mini-block */}
          <div className="p-5 sm:p-7 bg-[#FAF9F6]/60">
            <div className="text-[10px] uppercase tracking-widest text-ang-muted">Today&apos;s Revenue</div>
            <div className="font-serif text-4xl mt-1 tabular-nums" data-testid="sm-todays-revenue">
              {fmtInr(data.revenue.today, { compact: false })}
            </div>
            <div className="mt-4 space-y-1.5 text-sm">
              <ComparisonRow label="vs. yesterday"        pct={data.revenue.vs_yesterday_pct} />
              <ComparisonRow label="vs. same day last wk" pct={data.revenue.vs_last_week_pct} />
              <ComparisonRow label="vs. 30-day average"   pct={data.revenue.vs_30d_avg_pct} />
            </div>
          </div>
        </div>
      </section>

      {/* MIDDLE — health breakdown + trend sparkline */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-6">
        <HealthBreakdown health={data.health} flags={data.flags} />
        <TrendPanel trend={data.trend_14d} categories={data.top_categories} />
      </div>

      {/* ACTIONS */}
      <ActionsPanel actions={data.actions} onJump={(mod) => allowedModules.includes(mod) && navigate(`/app/${mod}`)} />

      {/* QUICK JUMP */}
      <section>
        <SectionHeader eyebrow="Open a module" title="Everything is already scoped to your store" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sm-jump-tiles">
          {jumpModules.map((m) => {
            const meta = MODULE_META[m];
            const Icon = meta.icon;
            return (
              <button
                key={m}
                onClick={() => navigate(`/app/${m}`)}
                data-testid={`sm-jump-${m}`}
                className="group ang-panel p-4 text-left hover:border-[#14142B] transition-colors flex items-start gap-3"
              >
                <span
                  className="shrink-0 w-9 h-9 flex items-center justify-center"
                  style={{ background: "rgba(20,20,43,0.06)", color: "#14142B" }}
                >
                  <Icon size={17} strokeWidth={1.6} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-serif text-base leading-tight">{meta.label}</div>
                  <div className="text-[11px] text-ang-muted mt-0.5">{meta.hint}</div>
                </div>
                <ChevronRight size={14} strokeWidth={1.8} className="text-ang-muted group-hover:text-[#14142B] transition-colors mt-1" />
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
};

// =====================================================================

const ComparisonRow = ({ label, pct }) => {
  const positive = pct >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  const color = positive ? "#2F8B5E" : "#B7402A";
  return (
    <div className="flex items-center justify-between">
      <span className="text-[11px] uppercase tracking-widest text-ang-muted">{label}</span>
      <span className="tabular-nums font-semibold inline-flex items-center gap-1" style={{ color }}>
        <Icon size={12} strokeWidth={2} />
        {fmtPct(pct)}
      </span>
    </div>
  );
};

const HealthBreakdown = ({ health, flags }) => {
  const entries = Object.entries(health.components); // [id, {score, max, label}]
  return (
    <section className="ang-panel" data-testid="sm-health-breakdown">
      <header className="border-b border-ang px-5 py-4 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-ang-muted">Health breakdown</div>
          <h3 className="font-serif text-lg mt-0.5">What&apos;s driving your score</h3>
        </div>
        <div className="text-right">
          <div className="font-serif text-3xl tabular-nums" style={{ color: BAND_COLOR[health.band] }}>{health.score}</div>
          <div className="text-[10px] uppercase tracking-widest text-ang-muted">of 100</div>
        </div>
      </header>
      <ul className="p-5 space-y-3">
        {entries.map(([id, c]) => {
          const pct = (c.score / c.max) * 100;
          const barColor =
            pct >= 78 ? BAND_COLOR.green :
            pct >= 62 ? BAND_COLOR.amber :
            BAND_COLOR.red;
          const flagged = flags[id] || (id === "stockouts" && flags.stockout);
          return (
            <li key={id} data-testid={`sm-comp-${id}`}>
              <div className="flex items-baseline justify-between mb-1">
                <span className="text-sm font-medium">
                  {c.label}
                  {flagged && (
                    <span
                      className="ml-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 font-semibold"
                      style={{ color: "#B7402A", background: "rgba(183,64,42,0.10)" }}
                    >
                      flagged
                    </span>
                  )}
                </span>
                <span className="text-xs text-ang-muted tabular-nums">
                  <span style={{ color: barColor, fontWeight: 600 }}>{c.score}</span>
                  <span className="mx-1">/</span>{c.max}
                </span>
              </div>
              <div className="h-2 bg-[#E7E4DC] relative overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 transition-[width] duration-500"
                  style={{ width: `${pct}%`, background: barColor }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

const TrendPanel = ({ trend, categories }) => {
  const chartData = trend.map((p) => ({
    day: new Date(p.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" }),
    revenue: p.revenue,
  }));
  const values = trend.map((p) => p.revenue);
  const min = Math.min(...values);
  const max = Math.max(...values);
  return (
    <section className="ang-panel">
      <header className="border-b border-ang px-5 py-4">
        <div className="text-[10px] uppercase tracking-widest text-ang-muted">Last 14 days</div>
        <h3 className="font-serif text-lg mt-0.5">Revenue trend &amp; top categories</h3>
      </header>
      <div className="p-3">
        <SizedChart height={180}>
          <LineChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
            <CartesianGrid stroke="#E7E4DC" strokeDasharray="3 4" vertical={false} />
            <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#6B6B7B" }} minTickGap={20} />
            <YAxis
              tickFormatter={(v) => (v >= 1e5 ? `${(v / 1e5).toFixed(1)}L` : v >= 1e3 ? `${(v / 1e3).toFixed(0)}K` : v)}
              axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#6B6B7B" }} width={40}
            />
            <Tooltip formatter={(v) => fmtInr(v, { compact: true })} />
            <Line type="monotone" dataKey="revenue" stroke="#14142B" strokeWidth={2} dot={{ r: 2, fill: "#14142B" }} activeDot={{ r: 4, fill: "#C9A15C" }} isAnimationActive={false} />
          </LineChart>
        </SizedChart>
      </div>
      <div className="px-5 pb-4 pt-1 border-t border-ang grid grid-cols-1 sm:grid-cols-3 gap-3">
        {categories.map((c) => (
          <div key={c.category_id} className="flex items-baseline justify-between gap-2" data-testid={`sm-topcat-${c.category_id}`}>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-ang-muted">{c.category}</div>
              <div className="font-serif text-base leading-tight truncate">{fmtInr(c.revenue_30d, { compact: true })}</div>
            </div>
            <div className="text-[11px] tabular-nums text-ang-muted">{c.share_pct}%</div>
          </div>
        ))}
      </div>
      <div className="px-5 pb-3 text-[10px] uppercase tracking-widest text-ang-muted flex items-center justify-between">
        <span>range · {fmtInr(min, { compact: true })} → {fmtInr(max, { compact: true })}</span>
        <span>categories · 30-day revenue</span>
      </div>
    </section>
  );
};

const ActionsPanel = ({ actions, onJump }) => {
  if (!actions.length) {
    return (
      <section className="ang-panel p-6 text-center text-sm text-ang-muted" data-testid="sm-actions-empty">
        Nothing urgent today — your store is running clean.
      </section>
    );
  }
  return (
    <section data-testid="sm-actions">
      <SectionHeader eyebrow="Actions Today" title="Prioritised for your morning walk-through" rightSlot={
        <span className="text-[10px] uppercase tracking-widest text-ang-muted hidden md:inline">tap to open the right module</span>
      } />
      <ul className="ang-panel divide-y divide-ang">
        {actions.map((a, i) => {
          const meta = PRIORITY_META[a.priority] || PRIORITY_META.low;
          const Icon = ACTION_ICON[a.kind] || AlertOctagon;
          return (
            <li key={i}>
              <button
                type="button"
                onClick={() => onJump(a.cta_module)}
                data-testid={`sm-action-${i}`}
                className="w-full px-4 sm:px-5 py-4 flex items-start gap-4 text-left hover:bg-[#FAF9F6] transition-colors"
              >
                <span
                  className="shrink-0 w-9 h-9 flex items-center justify-center border"
                  style={{ color: meta.color, background: meta.bg, borderColor: meta.border }}
                >
                  <Icon size={15} strokeWidth={2} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span
                      className="text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5"
                      style={{ color: meta.color, background: meta.bg }}
                    >
                      {meta.label}
                    </span>
                    <span className="text-[10px] uppercase tracking-widest text-ang-muted">{a.kind}</span>
                  </div>
                  <div className="font-serif text-base leading-snug">{a.title}</div>
                  <div className="text-[12px] text-ang-muted mt-0.5">{a.detail}</div>
                </div>
                <ArrowUpRight size={14} strokeWidth={1.8} className="text-ang-muted shrink-0 mt-1" />
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

const SectionHeader = ({ eyebrow, title, rightSlot }) => (
  <div className="flex items-end justify-between border-b border-ang pb-3 gap-3 flex-wrap mb-3">
    <div>
      <div className="text-[10px] uppercase tracking-widest text-ang-muted">{eyebrow}</div>
      <h2 className="font-serif text-2xl mt-1">{title}</h2>
    </div>
    {rightSlot}
  </div>
);

export default StoreManagerOverview;
